/** @file A to-scale sketch of where the checked layers land: one outlined, named box per layer (document px). */
import type { Translate } from '@pose2d/i18n/types';
import type { ImportDraft, LayerChoice } from './importTypes';

/** The PSD canvas, or the union of the placed PNG boxes (at least 1 x 1). */
function extentOf(draft: ImportDraft, shown: readonly LayerChoice[]): [number, number] {
  if (draft.source.kind === 'psd') return [draft.source.session.width, draft.source.session.height];
  return [shown.reduce((m, c) => Math.max(m, c.x + c.width), 1), shown.reduce((m, c) => Math.max(m, c.y + c.height), 1)];
}

/** Props of PlacementPreview. */
export interface PlacementPreviewProps {
  t: Translate;
  draft: ImportDraft;
}

/** Labels scale with the canvas so they stay readable at any document size. */
export function PlacementPreview({ t, draft }: PlacementPreviewProps) {
  const shown = draft.choices.filter((c) => c.checked);
  const [w, h] = extentOf(draft, shown);
  const font = Math.max(w, h) / 40;
  return (
    <svg className="import-preview" viewBox={`0 0 ${String(w)} ${String(h)}`} role="img" aria-label={t('psdImport.preview')}>
      <rect className="import-preview-page" width={w} height={h} />
      {shown.map((c) => (
        <g key={c.index}>
          <rect className="import-preview-layer" x={c.x} y={c.y} width={c.width} height={c.height} />
          <text className="import-preview-label" x={c.x + font / 4} y={c.y + font} fontSize={font}>{c.name}</text>
        </g>
      ))}
    </svg>
  );
}
